import React from 'react';
import { dayLong } from '../../lib/when';

/*
  The pieces all three Homes are built from.

  Written 5 September 2026, when the second and third Home were built, and the
  first was rewritten to use them. **That order was deliberate**: one Home does
  not tell you what is shared, three do, and pulling the shared parts out before
  building the second would have been guessing.

  It is also this job's own principle applied to itself. The reason the three
  dashboards were worth merging, in the owner's words, is that a change which has
  to be made in three places will be made in three places every time, forever.
  Three Homes with three copies of a progress bar would have started the same
  clock again on its first day.

  **What is shared and what is not.** The furniture is shared: the greeting, the
  card, the bar of four colours, a row of "n, what it is, one line, a way in".
  What each Home SAYS is not, because a rep asking "what do I owe" and an admin
  asking "what needs approving" are different questions, which is the whole
  reason the three Homes stayed three.
*/

const CHEV = (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

/*
  **The surface every Home is built out of, 12 September 2026.**

  The owner chose route B in `docs/design/depth-options/` after saying the
  flatness was one of his biggest concerns. The shadow is long, soft and pulled
  in with a negative spread, so it reads as height rather than as an outline: at
  rest you should not be able to point at it, only notice the card is above the
  page.

  **It is a constant because it was written out twice.** `Card` and `NeedsCard`
  carried the same four properties separately, so changing the depth of a Home
  meant changing it in two places and nothing would have said so if only one
  moved. Same recipe as `.card` in `index.css`; these are inline because these
  components predate that class and have never used it.
*/
/*
  **Corrected 12 September 2026, the day after it shipped.** Two faults, both
  found by measuring rather than looking, and both worst on a phone.

  **The shadow had no top and no sides.** An 18 pixel drop with a 20 pixel
  negative spread puts the shadow rectangle two pixels ABOVE the card's own top
  edge, so three sides of every card cast nothing at all. There are three
  shadows now: a hairline contact shadow that says the card is touching, a short
  one that gives the sides an edge, and the original long one for height.

  **And the edge was invisible.** A white border on Paper measures 1.04 to 1.
  It was chosen for a tinted ground, the phone had no tinted ground, and so a
  card's boundary was carried by nothing whatsoever.

  It is `--oh-border` again, the token the design record names for "dividers,
  card outlines (decorative)". **Measured, it is 1.25 to 1 on Paper**, which
  nobody would call a strong boundary. The first attempt at replacing the white
  one was an ad-hoc navy at 7 percent alpha and measured **1.09**, barely better
  than the 1.04 it replaced, which is why it is the token and not a number
  somebody picked. The edge is really carried by the contact shadow; the
  hairline's job is to stop the corner dissolving. The white survives where it
  belongs, as an inset highlight along the top where light would catch it.
*/
const SURFACE = {
  background: 'var(--surface-card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--oh-radius-card)',
  // The brand's resting lift, level 1 (rebrand step 3, guide 7.2). It was the
  // theme's card shadow from theme phase 2 on 14 September 2026.
  boxShadow: 'var(--depth-rest)',
};

export function Card({ children, pad = '18px 20px', style }) {
  return (
    <div style={{ ...SURFACE, padding: pad, ...style }}>
      {children}
    </div>
  );
}

export function Kind({ children }) {
  return <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{children}</div>;
}

// **Rule 29: the greeting belongs on Home and nowhere else.** The line under it
// is what makes it worth the room: it says what today is about rather than only
// saying hello, and every Home builds that line out of the same numbers its own
// blocks show, so the two can never disagree.
export function Greeting({ name, line }) {
  const first = name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const hello = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  return (
    <div className="oh-ground-panel" style={{ marginBottom: 18 }}>
      <div className="oh-title" style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-strong)', letterSpacing: '-0.5px' }}>{hello}, {first}</div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 5, lineHeight: 1.55 }}>{line}</div>
    </div>
  );
}

export function today() {
  return dayLong(new Date());
}

/*
  The four states of a count, in the four colours they have everywhere else in
  OnHand: not started is red, in progress is gold, submitted is blue, approved is
  green. A state with none of them draws no segment at all rather than a sliver,
  because a sliver reads as "a few" and the truth is "none".
*/
export function FourBar({ notStarted = 0, inProgress = 0, submitted = 0, approved = 0 }) {
  const total = notStarted + inProgress + submitted + approved;
  if (!total) return null;
  const seg = (n, bg) => (n > 0 ? <div style={{ width: (n / total * 100) + '%', background: bg }} /> : null);
  return (
    <>
      <div style={{ display: 'flex', height: 10, borderRadius: 'var(--oh-radius-pill)', overflow: 'hidden', gap: 2 }}>
        {seg(notStarted, 'var(--text-muted)')}
        {/* In progress blue, Submitted gold: guide 11.4, rebrand step 3. */}
        {seg(inProgress, 'var(--info)')}
        {seg(submitted, 'var(--accent)')}
        {seg(approved, 'var(--success)')}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 10, fontSize: 11, fontWeight: 700 }}>
        <span style={{ color: 'var(--text-secondary)' }}>{notStarted} Not started</span>
        <span style={{ color: 'var(--info-ink)' }}>{inProgress} In progress</span>
        <span style={{ color: 'var(--accent-ink)' }}>{submitted} Submitted</span>
        <span style={{ color: 'var(--success-ink)' }}>{approved} Approved</span>
      </div>
    </>
  );
}

// One row of "n, what it is, one line about it, and a way in".
//
// **A row with nothing in it is dimmed and does nothing**, rather than being
// hidden. Rule 9's habit: a queue that is empty today is a fact worth showing,
// and a list whose rows come and go is a list nobody learns the shape of.
//
// **`live` is for a row whose number is a measure rather than a queue**, added
// 18 September 2026 (the audit after the rebrand, issue 21). A region at 0% is
// the one slipping worst and a sheet with nothing counted is the one to start,
// and both were dimmed and could not be pressed, because 0 read as "empty".
export function Need({ n, title, sub, tone = 'var(--info-ink)', onClick, live = false }) {
  const zero = !live && (!n || n === '0%');
  return (
    <button type="button" onClick={onClick} disabled={zero}
      style={{ display: 'flex', alignItems: 'center', gap: 14, width: '100%', minHeight: 52, padding: '10px 2px', border: 'none', background: 'transparent', font: 'inherit', textAlign: 'left', cursor: zero ? 'default' : 'pointer', opacity: zero ? 0.45 : 1, borderTop: '1px solid var(--border-soft)' }}>
      <b style={{ fontSize: 20, fontWeight: 800, color: tone, minWidth: 34, flexShrink: 0 }}>{n}</b>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 1 }}>{sub}</span>
      </span>
      {!zero && CHEV}
    </button>
  );
}

// A card of Need rows with a count on its head and one way out of its foot.
// All three Homes have one; only what it counts differs.
export function NeedsCard({ title, count, rows, footer, onFooter }) {
  return (
    <div style={{ ...SURFACE, overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px 8px', display: 'flex', alignItems: 'center', gap: 9 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)' }}>{title}</div>
        {count != null && (
          <span style={{ fontSize: 11, fontWeight: 800, background: 'var(--info-bg)', color: 'var(--info-ink)', borderRadius: 'var(--oh-radius-pill)', padding: '2px 9px' }}>{count}</span>
        )}
      </div>
      <div style={{ padding: '0 20px 6px' }}>{rows}</div>
      {/* **Tertiary, not primary. Changed 11 September 2026**, job 27 phase 3.
          It was a solid blue bar across the foot of the card, which made two
          blue primary buttons on every Home and the guide's section 0 rule 3
          allows one. This is a way OUT of the card rather than the act the
          screen exists for, so it is the guide's tertiary: no fill, blue text,
          an Ice hover. */}
      {footer && (
        <button type="button" onClick={onFooter}
          className="oh-tertiary"
          style={{ display: 'block', width: '100%', minHeight: 'var(--oh-tap-min)', border: 'none', borderTop: '1px solid var(--border)', background: 'transparent', color: 'var(--link)', font: 'inherit', fontSize: 14, fontWeight: 600, padding: '0 20px', cursor: 'pointer' }}>
          {footer}
        </button>
      )}
    </div>
  );
}

// The three-block frame. The CSS in index.css settles what happens at each
// width; every Home hands it the same three children in the same order, so the
// order a person meets them in is one decision rather than three.
export function HomeFrame({ headline, needs, third }) {
  return (
    <div className="home-grid">
      <div className="home-main">{headline}{third}</div>
      <div className="home-side">{needs}</div>
    </div>
  );
}

/*
  How long is left, and what to call it.

  Added 5 September 2026, on the owner's decision. **Until that day nothing in
  OnHand was ever due**: a count was opened and stayed open until somebody closed
  it, so "what is overdue", which is a third of what a home page is for, could
  not be shown at all. A count can carry a day it should be finished by now, and
  all three Homes say the same thing about it because they all ask this.

  **The date is parsed by hand on purpose.** A date on its own, like 2026-09-30,
  is read by a browser as midnight in UTC, and in most of the United States that
  is the evening BEFORE. Handing it to the usual date reader would have printed
  the wrong day for everybody in Texas, which is everybody.

  A count with no date answers null and every screen simply says nothing, which
  is how all of them behaved before today.
*/
export function dueState(dueOn) {
  if (!dueOn) return null;
  const [y, m, d] = String(dueOn).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const due = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((due - today) / 86400000);
  // **Not put through the site's time zone, on purpose.** `due` is a plain day
  // already built out of its three numbers above. A day has no time in it to
  // convert, and converting one would move it.
  const words = due.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  const plural = n => (n === 1 ? '' : 's');

  if (days < 0) return {
    days, tone: 'late', words,
    chip: -days + ' day' + plural(-days) + ' overdue',
    line: 'It was due on ' + words + ', ' + -days + ' day' + plural(-days) + ' ago.',
  };
  if (days === 0) return { days, tone: 'soon', words, chip: 'Due today', line: 'It is due today.' };
  return {
    days, tone: days <= 7 ? 'soon' : 'ok', words,
    chip: 'Due in ' + days + ' day' + plural(days),
    line: 'It is due on ' + words + ', ' + days + ' day' + plural(days) + ' away.',
  };
}

const DUE_TONES = {
  late: { bg: 'var(--critical-bg)',   color: 'var(--critical-ink)' },
  soon: { bg: 'var(--warning-bg)', color: 'var(--warning-ink)' },
  ok:   { bg: 'var(--hover)', color: 'var(--text-secondary)' },
};

// The same chip on all three Homes. Red once the day has passed, amber in the
// last week, quiet before that: a deadline three months out is a fact, not a
// warning, and colouring it like one would make the colour mean nothing.
export function DueChip({ due }) {
  if (!due) return null;
  const t = DUE_TONES[due.tone];
  return (
    <span style={{ fontSize: 11, fontWeight: 800, background: t.bg, color: t.color, borderRadius: 'var(--oh-radius-pill)', padding: '3px 10px', whiteSpace: 'nowrap' }}>
      {due.chip}
    </span>
  );
}
